import React from 'react';

interface PasswordRequirement {
  name: string;
  description: string;
  met: boolean;
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

/**
 * Password strength indicator component
 *
 * Displays real-time feedback on password requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  const requirements: PasswordRequirement[] = [
    {
      name: 'min_length',
      description: 'At least 12 characters',
      met: password.length >= 12,
    },
    {
      name: 'uppercase',
      description: 'At least one uppercase letter (A-Z)',
      met: /[A-Z]/.test(password),
    },
    {
      name: 'lowercase',
      description: 'At least one lowercase letter (a-z)',
      met: /[a-z]/.test(password),
    },
    {
      name: 'number',
      description: 'At least one number (0-9)',
      met: /[0-9]/.test(password),
    },
    {
      name: 'special',
      description: 'At least one special character (!@#$%^&*)',
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ];

  const allRequirementsMet = requirements.every((req) => req.met);
  const passwordStrength = requirements.filter((req) => req.met).length;

  const getStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-gray-300';
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return 'No password';
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 4) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">Password Strength</span>
          <span className={`text-sm font-semibold ${
            allRequirementsMet ? 'text-green-600' :
            passwordStrength > 2 ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {getStrengthText()}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${(passwordStrength / requirements.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700">Password must contain:</p>
        <ul className="space-y-1">
          {requirements.map((req) => (
            <li
              key={req.name}
              className={`flex items-center text-sm ${
                req.met ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              <svg
                className={`w-4 h-4 mr-2 ${req.met ? 'text-green-500' : 'text-gray-400'}`}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {req.met ? (
                  <path d="M5 13l4 4L19 7" />
                ) : (
                  <circle cx="12" cy="12" r="10" />
                )}
              </svg>
              {req.description}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
